import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReturnButtons from '../../components/core/ReturnButtons';
import { makeStyles, ThemeProvider, useTheme } from '@material-ui/core/styles';
import { Box, Grid, Paper, Typography, IconButton, Collapse } from '@material-ui/core';
import { ExpandLess, ExpandMore, LayersOutlined, Waves } from '@material-ui/icons';
import GoogleMap from '../../components/map/GoogleMap';
import BackgroundMapPanel from '../../components/map/BackgroundMapPanel';
import OverlayPanelsToggle from '../../components/map/OverlayPanelsToggle';
import ImageChooserCard from '../../components/map/ImageChooserCard';
import { ShapeList } from '../../components';
import LoadedImagesAccordion from '../../components/map/LoadedImagesAccordion';
import { Actions as Map } from '../../../store/ducks/map';
import { Actions as Dialog } from '../../../store/ducks/dialog';
import { useTranslation } from 'react-i18next';
import TourGuider from '../../components/tour/TourGuider';
import { useGuidedTour, useLocalStorage } from '../../../common/utils';
import { Button } from '@material-ui/core';
import CoastlineAnalysisSteps from '../../components/steps/CoastlineAnalysisSteps';
import MeanTransectButton from '../../components/visualization/MeanTransectButton';
import HovmollerPlot from '../../components/visualization/Hovmoller';
import AnalysisSteps from '../../components/steps/AnalysisSteps';
import { Actions as Imagery } from '../../../store/ducks/imagery';
import { renderPendingImportedShapes, restorePendingBaseline } from '../../../algorithms/shoreline/sessionExport';
import {
  buildMapStyles,
  createShorelineTheme,
  OVERLAY_COLUMN_MAX_HEIGHT,
  OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
  OVERLAY_SIDE_GUTTER,
  OVERLAY_SCROLLBAR_GUTTER,
  OVERLAY_COLUMN_PADDING_TOP,
  OVERLAY_PANEL_GAP,
  OVERLAY_MAIN_COLUMN_WIDTH,
  OVERLAY_SIDE_COLUMN_WIDTH,

 OVERLAY_COLUMN_LAYOUT,
  fitBoundsWithOverlayPadding,
} from './theme';
import { useThemeMode } from '../../contexts/index.jsx';
import { aoiPaths } from '../../../common/aoi';

/**
 * @function ProcessingPage
 * @description
 * Shoreline component for exhibit satellite images, map visualization, region of interest (ROI) definition, and coastline analysis.
 * @see {@link GoogleMap} - Google Map component
 * @see {@link ImageChooserCard} - Image selection component
 * @see {@link CoastlineAnalysisSteps} - Coastline analysis component
 * @see {@link TourGuider} - Interface guided tour component
 * **Called by:
 * @see {MainPage} - via the `/shoreline/result` and `/processing` Routes (imported as ProcessingShorelinePage).
 * @memberof module:Shoreline
 * @returns {JSX.Element} Complete processing page JSX element
 */
const ProcessingPage = () => {
  const coordinates = useSelector((state) => state.acquisition.coordinates);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [t] = useTranslation();
  const mapRef = useRef();
  const boundsRef = useRef();
  const ROI = useRef();
  const [baselineVisible, setBaselineVisible] = useState(true);
  const [baseLine, setBaseLine] = useState(null);
  const drawingManager = useRef();
  const shapesGroup = useSelector((state) => state.map.shapes);
  const selectedImages = useSelector((state) => state.results.chosenImages);

  /**
   * @function handleBaselineVisibility
   * @description Toggles the drawn baseline's visibility on the map by updating its stroke opacity,
   * and keeps the local baselineVisible state in sync.
   * @param {boolean} visible - Whether the baseline should be shown.
   * **Called by:
   * @see {ShapeList} - via the handleBaselineVisibility prop.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const handleBaselineVisibility = (visible) => {
    setBaselineVisible(visible);
    baseLine?.overlay?.setOptions({
      strokeOpacity: visible ? 1 : 0,
    });
  };

  /**
   * @function displayROI
   * @description Responsible for showing on screen the region of interest (ROI)
   * **Called by:
   * @see {ProcessingPage} - via the GoogleMap's onLoad callback.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const displayROI = () => {
    if (!coordinates) return;
    dispatch(Map.centralizeMap(coordinates));
    const formatedCoordinates = aoiPaths(coordinates);
    ROI.current = new window.google.maps.Polygon({
      paths: formatedCoordinates,
      geodesic: true,
      fillColor: 'transparent',
      strokeColor: '#00A391',
      strokeOpacity: 0.6,
      strokeWeight: 5,
      clickable: false,
    });
    ROI.current.setMap(mapRef.current);
    boundsRef.current = new window.google.maps.LatLngBounds();
    ROI.current
      .getPaths()
      .getArray()
      .flatMap((path) => path.getArray())
      .forEach((coord) => {
        let latLng = new window.google.maps.LatLng(coord.lat(), coord.lng());
        boundsRef.current.extend(latLng);
      });
    fitBoundsWithOverlayPadding(mapRef.current, boundsRef.current);
  };

  const steps = [
    {
      selector: '#mapScreen',
      content: t('tour.map.start'),
    },
    {
      selector: '#imageChooserForm',
      content: t('tour.map.imageChooser.info'),
    },
    {
      selector: '#imageChooserSelect',
      content: t('tour.map.imageChooser.select'),
    },
    {
      selector: '#imageChooserLoadButton',
      content: t('tour.map.imageChooser.click'),
    },
  ];

  const performAnalysis = async (baseLineCoords, transectsStep, transectsExtent, threshold) => {
    return await dispatch(
      Imagery.performAnalysis(baseLineCoords, transectsStep, transectsExtent, selectedImages, threshold),
    );
  };

  const [isTourOpen, setIsTourOpen] = useGuidedTour('showMapTour');
  const [layersPanelOpen, setLayersPanelOpen] = useLocalStorage('shorelineLayersPanelOpen', true);
  const [mapType, setMapType] = useLocalStorage('shorelineMapType', 'roadmap');
  const [labelsVisible, setLabelsVisible] = useLocalStorage('shorelineMapLabels', true);
  const outerTheme = useTheme();
  const { shorelineThemeMode } = useThemeMode();
  const shorelineTheme = useMemo(
    () => createShorelineTheme(shorelineThemeMode, outerTheme),
    [outerTheme, shorelineThemeMode],
  );

  /**
   * @function handleMapTypeChange
   * @description Switches the live Google Maps instance's base layer (roadmap vs. satellite) from
   * the compact "Mapa de fundo" panel, without touching the map's own native map-type control.
   * @param {String} type - `'roadmap'` or `'satellite'`.
   * **Called by:
   * @see {ProcessingPage} - via the "Mapa de fundo" panel's options.
   * @memberof module:Shoreline
   * @returns {void}
   */
  const handleMapTypeChange = (type) => {
    setMapType(type);
    mapRef.current?.setMapTypeId(type);
  };

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({ styles: buildMapStyles({ themeMode: shorelineThemeMode, mapType, labelsVisible }) });
  }, [shorelineThemeMode, mapType, labelsVisible]);

  return (
    <ThemeProvider theme={shorelineTheme}>
    <Box className={classes.wrapper}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={0}
        id="mapScreen"
        className={classes.mapOverlay}>
        <Grid
          item
          xs={3}
          className={classes.mapContainer}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: 8,
            flexBasis: 'auto',
            flexGrow: 0,
            width: OVERLAY_MAIN_COLUMN_WIDTH,
            maxWidth: OVERLAY_MAIN_COLUMN_WIDTH,
            paddingTop: OVERLAY_COLUMN_PADDING_TOP,
            paddingLeft: OVERLAY_SIDE_GUTTER,
            paddingRight: OVERLAY_SCROLLBAR_GUTTER,
            height: '100%',
            overflowY: 'auto',
            maxHeight: OVERLAY_COLUMN_MAX_HEIGHT_SAFE,
          }}>
          <OverlayPanelsToggle side="left" />
          <Paper className={classes.panelWrapper} elevation={1}>
            <Box className={classes.panelHeader}>
              <Box display="flex" alignItems="center" gridGap={6}>
                <LayersOutlined fontSize="small" color="primary" />
                <Typography variant="h6">{t('forms.shorelineAnalysis.layersPanelTitle')}</Typography>
              </Box>
              <IconButton size="small" onClick={() => setLayersPanelOpen((prev) => !prev)}>
                {layersPanelOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Box>
            <Collapse in={layersPanelOpen}>
              <ShapeList
                embedded
                shapesGroup={shapesGroup}
                baseLine={baseLine}
                roiCoords={ROI}
                drawingManager={drawingManager}
                baselineVisible={baselineVisible}
                handleBaselineVisibility={handleBaselineVisibility}
              />
            </Collapse>
          </Paper>
          <AnalysisSteps
            steps={['SELECT_IMAGES', 'DRAW_BASELINE', 'CONFIGURE_TRANSECTS', 'ANALYSIS_DONE']}
            t={t}
            mapRef={mapRef}
            roiCoords={ROI}
            drawingManager={drawingManager}
            onAnalysisPerform={performAnalysis}
          />
          {shapesGroup?.length > 0 && (
            <Paper className={classes.analysisActions} elevation={1}>
              <Button
                fullWidth
                color="primary"
                variant="contained"
                startIcon={<Waves />}
                onClick={() => dispatch(Dialog.open('coastlineEvolution'))}>
                {t('forms.acquisition.5.bathymetry.shorelineAnalysis')}
              </Button>
              <MeanTransectButton />
              <HovmollerPlot />
            </Paper>
          )}
        </Grid>
        <Grid
          item
          xs={2}
          style={{
            display: 'flex',
            flexDirection: 'column',
            ...OVERLAY_COLUMN_LAYOUT,
            gap: 8,
            flexBasis: 'auto',
            flexGrow: 0,
            width: OVERLAY_SIDE_COLUMN_WIDTH,
            maxWidth: OVERLAY_SIDE_COLUMN_WIDTH,
            marginLeft: 'auto',
            paddingTop: OVERLAY_COLUMN_PADDING_TOP,
            paddingLeft: OVERLAY_SCROLLBAR_GUTTER,
            paddingRight: OVERLAY_SIDE_GUTTER,
            overflowY: 'auto',
            maxHeight: OVERLAY_COLUMN_MAX_HEIGHT,
          }}>
          <OverlayPanelsToggle side="right" />
          <BackgroundMapPanel
            mapType={mapType}
            onMapTypeChange={handleMapTypeChange}
            labelsVisible={labelsVisible}
            onLabelsVisibleChange={setLabelsVisible}
            t={t}
          />
          <LoadedImagesAccordion />
          <ImageChooserCard vertical="shoreline" />
        </Grid>
      </Grid>
      <ReturnButtons lastStepPath="/main/shoreline/3" />
      <GoogleMap
        onLoad={(map) => {
          mapRef.current = map;
          map.setMapTypeId(mapType);
          displayROI(map);
          const restoredBaseline = restorePendingBaseline(map);
          if (restoredBaseline) setBaseLine(restoredBaseline);
          renderPendingImportedShapes(dispatch);
        }}
        onBoundsChanged={(bounds) => {
          boundsRef.current = bounds;
        }}
        style={{ position: 'absolute' }}
        mapOptions={{ mapTypeControl: false }}
      />
      <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
    </Box>
    </ThemeProvider>
  );
};

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles.
 * Defines CSS-in-JS styles for processing page components.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Shoreline
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxHeight: '100vh',
    flexGrow: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  mapContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexFlow: 'column',
    ...OVERLAY_COLUMN_LAYOUT,
  },
  analysisActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: OVERLAY_PANEL_GAP,
    padding: 12,
    width: '100%',
  },
  panelWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flexShrink: 0,
    overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  mapOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '100%',
    zIndex: 10,
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    },
  },
  margin: {
    margin: '5px',
  },
  right: {
    textAlign: 'right',
  },
}));

export default ProcessingPage;
